import clsx from "clsx";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { To, useSearchParams } from "react-router-dom";

import { useAppSelector } from "@cms/utils/store/hooks";
import { ISystemBase, ISystemResponse, TSubtype, checkRouteRequiredRole } from "@dl/declarations";
import { generateSearchParam, renderDateTime, renderTime } from "../../utils/helpers";
import { RenderModeType, TableColumn, TableColumns } from "../../utils/types";
import { WrappedLoader } from "../ui/Loader";
import { Button } from "./Button";
import { Dropdown } from "./Dropdown";
import { PaginationV3 } from "./Pagination";
import { StaticInput } from "./StaticInput";
import { SystemTextInput } from "./TextInput";

import { ALLOWED_LIMITS, FIXED_SEARCH_PARAMS } from "@cms/utils/constants";
import { FaSortAlphaDown, FaSortAlphaDownAlt } from "react-icons/fa";
import { IoSearchOutline } from "react-icons/io5";
import { ResetFiltersIcon } from "../icons";
import { SystemBackdrop } from "../ui/Backdrop";
import { SystemCheckbox } from "./Checkbox";
import { SystemSwitch } from "./Switch";

const TABLE_SIZES = {
    "sm": 'h-fit md:h-48',
    "md": 'h-fit md:h-72',
    "lg": 'h-fit md:h-96',
    "xl": 'h-fit md:h-120',
    "2xl": 'h-fit md:h-144',
    "fit": 'h-fit',
    "full": 'h-fit md:h-full',
} as const

const NO_OF_COLUMNS = {
    "1": "md:grid-cols-1",
    "2": "md:grid-cols-2",
    "3": "md:grid-cols-3",
    "4": "md:grid-cols-3 lg:grid-cols-4",
    "5": "md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5",
} as const

type FiltersSelectType = {
    type: 'select',
} & Pick<React.ComponentProps<typeof Dropdown>, 'options' | 'allowMultiSelect' | 'searchOptions' | 'distributionOptions' | 'shouldShortenLabel'>

type FiltersDateType = {
    type: 'date';
    minDate?: string;
    maxDate?: string;
}

type FilterStaticType = {
    type: 'static',
}

type FilterBooleanType = {
    type: 'boolean',
    onValue: string,
    offValue: string,
}

type FilterCheckboxType = {
    type: 'checkbox',
    onValue: string,
    offValue: string,
}

type FilterSwitchType = {
    type: 'switch',
    onValue: string,
    offValue: string,
}

type FilterCustomType = {
    type: 'custom',
    component: React.ReactNode,
}

type FilterNoneType = {
    type: 'none',
}

type Filters = (FiltersSelectType | FiltersDateType | FilterStaticType | FilterBooleanType | FilterCheckboxType | FilterSwitchType | FilterCustomType | FilterNoneType) & {
    label: string,
    hide?: boolean,
    defaultValue?: string,
};

type DistributionRowProps = {
    type?: 'row';
    columns: TableColumns;
    renderItem?: never;
}

type DistributionGridProps = {
    type?: 'grid';
    noOfColumns?: keyof typeof NO_OF_COLUMNS;
    renderItem: (row: any, index: number) => React.ReactNode;
    columns?: never
}

type Props<TItem> = {
    tableSize?: keyof typeof TABLE_SIZES;

    data: ISystemResponse<Array<TItem>>
    error: Error | null;

    distribution: DistributionRowProps | DistributionGridProps

    pageSize?: typeof ALLOWED_LIMITS[keyof typeof ALLOWED_LIMITS];

    isLoading?: boolean;

    basePath: string;
    showPaginationOnTop?: boolean;
    showPaginationOnBottom?: boolean;
    filters?: Record<string, Filters>
    searchOptions?: {
        allow: boolean
        label?: string
        path?: To
        btnOptions?: Partial<Pick<React.ComponentProps<typeof Button>, "leftIcon" | "rightIcon">>
    };
    updateOptions?: {
        allow: boolean;
        hide?: boolean;
        label?: string
        path?: To
        btnOptions?: Partial<Pick<React.ComponentProps<typeof Button>, "leftIcon" | "rightIcon">>
    };
    createOptions?: {
        allow: boolean
        hide?: boolean
        label?: string
        path?: To
        btnOptions?: Partial<Pick<React.ComponentProps<typeof Button>, "leftIcon" | "rightIcon">>
        component?: React.ReactNode
    };
    form?: React.ReactNode;
    defaultSearchParams?: Record<string, string>;

    subtypes?: Array<TSubtype>;
}

export const Table = <TItem extends ISystemBase,>(props: Props<TItem>) => {
    const {
        distribution,
        tableSize = 'lg',

        isLoading = false,

        basePath,
        filters = {},
        defaultSearchParams = {},
        showPaginationOnTop = true,
        showPaginationOnBottom = false,
        searchOptions = {
            allow: false,
            btnOptions: {},
        },
        createOptions = {
            allow: false,
            btnOptions: {},
        },
        updateOptions = {
            allow: false,
            btnOptions: {},
        },
        form,
        subtypes = [],

        pageSize = ALLOWED_LIMITS["10"],

        data,
        error,
    } = props;

    const { profile } = useAppSelector((state) => state.user)

    // const location = useLocation()
    const [searchParams, setSearchParams] = useSearchParams({
        [generateSearchParam(basePath, 'limit')]: String(pageSize),
        ...defaultSearchParams
    })

    const mode = searchParams.get(generateSearchParam(basePath, 'mode')) as RenderModeType;
    const selectedSubtype = searchParams.get(generateSearchParam(basePath, 'subtypes'));
    const limit = Number(searchParams.get(generateSearchParam(basePath, 'limit')) || pageSize);
    const skip = Number(searchParams.get(generateSearchParam(basePath, 'skip')) || 0);
    const [sortKey, sortValue] = (searchParams.get(generateSearchParam(basePath, 'sort')) || '').split(':');

    const [searchInput, setSearchInput] = useState<string>("");

    const handleResetFilters = useCallback(() => {
        setSearchParams((prevValue) => {
            Object.keys(filters).forEach((key) => {
                prevValue.delete(generateSearchParam(basePath, key));
            });

            prevValue.delete(generateSearchParam(basePath, 'skip'));

            prevValue.set(generateSearchParam(basePath, 'mode'), 'view');
            prevValue.set(generateSearchParam(basePath, FIXED_SEARCH_PARAMS.SHOULD_REFRESH), 'true');

            Object.keys(filters).forEach((key) => {
                if (filters[key].defaultValue) {
                    prevValue.set(generateSearchParam(basePath, key), filters[key].defaultValue);
                }
            });

            return prevValue;
        })
    }, [pageSize, setSearchParams]);

    const handleSearch = useCallback(() => {
        setSearchParams((prevValue) => {
            prevValue.delete(generateSearchParam(basePath, 'skip'));
            prevValue.set(generateSearchParam(basePath, 'query'), searchInput);
            return prevValue;
        });
    }, [searchInput, setSearchParams]);

    const handleSort = useCallback(
        (col: TableColumn) => {
            if (col.allowSort) {
                const newSort =
                    sortKey === col.key && sortValue === "desc"
                        ? `${col.key}:asc`
                        : `${col.key}:desc`;

                setSearchParams((prevValue) => {
                    prevValue.set(generateSearchParam(basePath, 'sort'), newSort);
                    return prevValue;
                });
            }
        },
        [sortKey, sortValue, setSearchParams]
    );

    const renderField = useCallback((col: TableColumn, row: any) => {
        const key = col.key || '';
        const value = row[key as keyof typeof row];

        if (col.renderField) {
            return col.renderField(row, value)
        }

        if (value === null || value === undefined) {
            return 'N/A'
        }

        switch (col.type) {
            case "datetime": {
                return renderDateTime(value);
            }

            case "time": {
                return renderTime(value);
            }


            case "text":
            case "number":
            default: {
                return String(value);
            }
        }
    }, []);

    const renderSortIcon = useCallback((col: TableColumn) => {
        if (col.allowSort && sortKey === col.key) {
            return sortValue === 'asc' ? (
                <FaSortAlphaDown className="w-4 h-4" />
            ) : (
                <FaSortAlphaDownAlt className="w-4 h-4" />
            );
        }

        return null;
    }, [sortKey, sortValue]);

    const renderFilters = () => {
        const filterEntries = Object.entries(filters);

        return (
            <div
                className={clsx(
                    "flex flex-col",
                    "py-2 gap-2 border-b-cs-normal-gray border-b"
                )}
            >
                <div className="flex flex-wrap items-end gap-2">
                    {subtypes.length > 0 && (
                        <Dropdown
                            options={subtypeOptions}
                            label=""
                            value={selectedSubtype || "all"}
                            handleChange={(option) => {
                                setSearchParams((prevValue) => {
                                    prevValue.delete(generateSearchParam(basePath, 'skip'));
                                    prevValue.set(generateSearchParam(basePath, 'subtypes'), option.slug);
                                    return prevValue;
                                });
                            }}
                            searchOptions={{
                                allow: false
                            }}
                            allowSingleDeselect={false}
                            allowMultiSelect={false}
                        />
                    )}

                    <div className="flex flex-wrap flex-row items-end gap-2">
                        {searchOptions.allow && (
                            <SystemTextInput
                                type="search"
                                label=""
                                placeholder={searchOptions.label || "Search topic"}
                                inputSize="lg"
                                value={searchInput}
                                disabled={isLoading}
                                onChange={(e) => setSearchInput(e.target.value)}
                                rightIcon={<IoSearchOutline />}
                            />
                        )}
                    </div>

                    <div className="flex flex-wrap flex-row items-end gap-2">
                        {
                            filterEntries.map(([key, filter]) => {
                                if (filter.hide) {
                                    return null
                                }

                                switch (filter.type) {
                                    case 'select': {
                                        const { options, label, ...restOfFilter } = filter;
                                        return (
                                            <Dropdown
                                                key={key}
                                                label={label}
                                                options={options}
                                                value={searchParams?.getAll(generateSearchParam(basePath, key))?.join(',')}
                                                handleChange={(option) => {
                                                    setSearchParams((prevValue) => {
                                                        prevValue.delete(generateSearchParam(basePath, 'skip'));
                                                        prevValue.delete(generateSearchParam(basePath, key));
                                                        (option.slug)?.split(',')?.forEach((slug) => {
                                                            prevValue.append(generateSearchParam(basePath, key), slug);
                                                        });
                                                        return prevValue;
                                                    });
                                                }}
                                                maxChipsCount={2}
                                                disabled={isLoading}
                                                {...restOfFilter}
                                            />
                                        )
                                    }

                                    case 'static': {
                                        return (
                                            <StaticInput
                                                key={key}
                                                label={filter.label}
                                                values={(searchParams.get(generateSearchParam(basePath, key)) || "").split(",").filter(Boolean)}
                                                handleChange={(newValues) => {
                                                    setSearchParams((prevValue) => {
                                                        prevValue.delete(generateSearchParam(basePath, 'skip'));
                                                        prevValue.set(generateSearchParam(basePath, key), newValues.join(","));
                                                        return prevValue;
                                                    });
                                                }}
                                                disabled={isLoading}
                                                maxChipsCount={1}
                                            />
                                        )
                                    }

                                    // case 'date': {
                                    //     return (
                                    //         <SystemTextInput
                                    //             key={key}
                                    //             type="date"
                                    //             min={filter.minDate}
                                    //             max={filter.maxDate}
                                    //             description={filter.label}
                                    //             inputSize="xs"
                                    //             value={searchParams.get(generateSearchParam(basePath, key)) || ""}
                                    //             disabled={isLoading}
                                    //             onChange={(e) => {
                                    //                 const date = e.target.value;
                                    //                 if (date !== searchParams.get(generateSearchParam(basePath, key))) {
                                    //                     setSearchParams((prevValue) => {
                                    //                         prevValue.delete(generateSearchParam(basePath, 'skip'));
                                    //                         prevValue.set(generateSearchParam(basePath, key), date);
                                    //                         return prevValue;
                                    //                     });
                                    //                 }
                                    //             }}
                                    //         />
                                    //     )
                                    // }

                                    case 'switch': {
                                        let usedValue = ""

                                        if (filter.defaultValue) {
                                            usedValue = filter.defaultValue
                                        }

                                        const searchParamValue = searchParams.get(generateSearchParam(basePath, key));

                                        if (searchParamValue) {
                                            usedValue = searchParamValue;
                                        }

                                        return (
                                            <SystemSwitch
                                                key={key}
                                                label={filter.label}
                                                value={filter.onValue === usedValue}
                                                handleToggle={(checked) => {
                                                    setSearchParams((prevValue) => {
                                                        prevValue.delete(generateSearchParam(basePath, 'skip'));
                                                        prevValue.set(generateSearchParam(basePath, key), checked ? filter.onValue : filter.offValue);
                                                        return prevValue;
                                                    });
                                                }}
                                            />
                                        )
                                    }

                                    case 'checkbox': {
                                        let usedValue = ""

                                        if (filter.defaultValue) {
                                            usedValue = filter.defaultValue
                                        }

                                        const searchParamValue = searchParams.get(generateSearchParam(basePath, key));

                                        if (searchParamValue) {
                                            usedValue = searchParamValue;
                                        }

                                        return (
                                            <div
                                                key={key}
                                                className="self-center"
                                            >
                                                <SystemCheckbox
                                                    label={filter.label}
                                                    checked={filter.onValue === usedValue}
                                                    handleChange={(checked) => {
                                                        setSearchParams((prevValue) => {
                                                            prevValue.delete(generateSearchParam(basePath, 'skip'));
                                                            prevValue.set(generateSearchParam(basePath, key), checked ? filter.onValue : filter.offValue);
                                                            return prevValue;
                                                        });
                                                    }}
                                                />
                                            </div>
                                        )
                                    }

                                    case 'boolean': {
                                        return null
                                    }

                                    case 'custom': {
                                        return (
                                            <React.Fragment key={key}>
                                                {filter.component}
                                            </React.Fragment>
                                        )
                                    }

                                    case 'none':
                                    default: {
                                        return null
                                    }
                                }
                            })
                        }
                    </div>

                    <div className="flex flex-wrap flex-row items-end gap-2">
                        <div className="flex flex-wrap gap-2">
                            <Button
                                as="button"
                                onClick={handleResetFilters}
                                className="order-last lg:order-first"
                                disabled={isLoading}
                                shape="square"
                                inputVariant="white_text_deep_ocean_bg"
                            >
                                <ResetFiltersIcon className="m-1" />
                            </Button>
                        </div>

                        {(userHasPermissionsToCreate && createOptions.allow) && (
                            <>
                                {createOptions.component ? (
                                    createOptions.component
                                ) : (
                                    <Button
                                        as="link"
                                        to={createOptions.path || {
                                            search: `?${generateSearchParam(basePath, 'mode', 'create')}`,
                                        }}
                                        inputVariant="white_text_blue_bg"
                                        {...(createOptions.btnOptions || {})}
                                    >
                                        {createOptions.label || 'Create'}
                                    </Button>
                                )}
                            </>
                        )}
                    </div>

                    {
                        showPaginationOnTop && (
                            <div className="ms-auto">
                                <PaginationV3
                                    pagination={{
                                        limit,
                                        skip,
                                    }}
                                    isLoading={isLoading}
                                    total_count={data.total_count}
                                    basePath={basePath}
                                />
                            </div>
                        )
                    }
                </div>
            </div>
        )
    }

    const renderContent = () => {
        if (
            ((mode === 'create') || (mode === 'update'))
        ) {
            return form
        }
        else {
            return (
                <React.Fragment>
                    <SystemBackdrop isOpen={isLoading}>
                        <WrappedLoader loaderSize='lg' loaderColor='white' />
                    </SystemBackdrop>

                    {renderFilters()}


                    {error ? (
                        <div className="flex justify-center p-4 text-red-500">
                            {error.message}
                        </div>
                    ) : (
                        <div className="flex flex-col h-full w-full">
                            {hasData ? (
                                <div
                                    className={clsx(
                                        // "h-[65vh]",
                                        "overflow-x-scroll md:overflow-x-clip"
                                    )}
                                >
                                    {distribution.type === 'row' ? renderedDistributionAsRow : null}
                                    {distribution.type === 'grid' ? renderedDistributionAsGrid : null}
                                </div>
                            ) : (
                                <div className="flex justify-center items-center h-full my-8">
                                    <p>No data available</p>
                                </div>
                            )}

                            {
                                (showPaginationOnBottom && hasData) && (
                                    <div className="w-full flex justify-end my-2">
                                        <PaginationV3
                                            pagination={{
                                                limit,
                                                skip,
                                            }}
                                            isLoading={isLoading}
                                            total_count={data.total_count}
                                            basePath={basePath}
                                        />
                                    </div>
                                )
                            }
                        </div>
                    )}
                </React.Fragment>
            )
        }
    }

    const subtypeOptions = useMemo(() => {
        return [{ _id: '1', slug: 'all', label: 'All', groupKey: 'Subtypes' }, ...subtypes.map((subtype) => ({
            _id: subtype.ID,
            slug: subtype.SLUG,
            label: subtype.NAME,
            groupKey: 'Subtypes'
        }))]
    }, [subtypes])

    const userHasPermissionsToCreate = useMemo(() => {
        return checkRouteRequiredRole(basePath, 'c', profile.permissions)
    }, [basePath, profile.permissions])

    const userHasPermissionsToUpdate = useMemo(() => {
        return checkRouteRequiredRole(basePath, 'u', profile.permissions)
    }, [basePath, profile.permissions])

    const hasData = useMemo(() => {
        if (isLoading) return true
        if (data.data.length > 0) return true

        return false
    }, [isLoading, data])

    const renderedDistributionAsRow = useMemo(() => {
        if (distribution.columns)
            return (
                <table className="w-full text-left">
                    <thead className="w-full rounded-lg text-base text-white font-semibold">
                        <tr className="static md:sticky top-0 z-30 bg-white shadow-xl">
                            <th className="py-3 px-3 text-[#212B36] sm:text-base font-bold whitespace-nowrap">
                                #
                            </th>

                            {Object.values(distribution.columns).map((col) => (
                                <th
                                    key={col.key}
                                    className={clsx(
                                        "group",
                                        "py-3 px-3 text-[#212B36] sm:text-base font-bold whitespace-nowrap",
                                        sortKey === col.key && "flex items-center gap-x-2",
                                        col.allowSort && "cursor-pointer"
                                    )}
                                    onClick={() => handleSort(col)}
                                >
                                    {renderSortIcon(col)}
                                    {col.label}
                                </th>
                            ))}

                            {
                                (userHasPermissionsToUpdate && updateOptions.allow && !updateOptions.hide) && (
                                    <th
                                        className="py-3 px-3 text-[#212B36] sm:text-base font-bold whitespace-nowrap"
                                    >
                                        {updateOptions.label || 'Update'}
                                    </th>
                                )
                            }
                        </tr>
                    </thead>
                    <tbody>
                        {data.data.map((row, index) => (
                            <tr key={row._id || index} className="border-b border-black">
                                <td key={`row-${row?._id}-${index}`}>{index + 1}</td>

                                {Object.values(distribution.columns).map((col) => (
                                    <td key={col.key} className="py-3 px-3 max-w-32 md:max-w-64 xl:max-w-96">
                                        {renderField(col, row)}
                                    </td>
                                ))}

                                {
                                    (userHasPermissionsToUpdate && updateOptions.allow && !updateOptions.hide) && (
                                        <td className="py-3 px-3">
                                            <Button
                                                to={updateOptions.path || {
                                                    search: `?${generateSearchParam(basePath, "mode", "update")}&${generateSearchParam(basePath, "id", row._id)}`
                                                }}
                                                as="link"
                                                inputVariant="deep_ocean_text_white_bg"
                                            >
                                                {updateOptions.label || 'Update'}
                                            </Button>
                                        </td>
                                    )
                                }
                            </tr>
                        ))}

                    </tbody>
                </table>
            )

        return null
    }, [data, distribution, updateOptions, basePath])

    const renderedDistributionAsGrid = useMemo(() => {
        if (distribution.renderItem)
            return (
                <div
                    className={clsx(
                        "grid grid-cols-1 gap-2 my-2",
                        NO_OF_COLUMNS[distribution.noOfColumns || "4"]
                    )}
                >
                    {data.data.map((row, index) => (
                        <div
                            key={row._id || index}
                            className="flex flex-col gap-2"
                        >
                            {distribution.renderItem(row, index)}

                            {
                                (userHasPermissionsToUpdate && updateOptions.allow && !updateOptions.hide) && (
                                    <Button
                                        as="link"
                                        to={{
                                            search: `?${generateSearchParam(basePath, "mode", "update")}&${generateSearchParam(basePath, "id", row._id)}`
                                        }}
                                    >
                                        {updateOptions.label || 'Update'}
                                    </Button>
                                )
                            }
                        </div>
                    ))}
                </div>
            );

        return null
    }, [data, distribution, updateOptions, basePath]);

    useEffect(() => {
        const timerId = setTimeout(() => {
            const searchQuery = searchParams.get(generateSearchParam(basePath, 'query')) || "";

            if (searchInput.length > 2) {
                handleSearch();
            }

            if (
                searchInput.length === 0 &&
                searchInput !== searchQuery
            ) {
                handleSearch();
            }
        }, 500);

        return () => {
            clearTimeout(timerId);
        }
    }, [searchInput])

    useEffect(() => {
        const searchQuery = searchParams.get(generateSearchParam(basePath, 'query')) || "";
        setSearchInput(searchQuery);
    }, [searchParams])

    useEffect(() => {
        if (userHasPermissionsToCreate && mode === 'create' && !createOptions.allow) {
            setSearchParams({
                [generateSearchParam(basePath, 'mode')]: 'view'
            })
        }
        else if (userHasPermissionsToUpdate && mode === 'update' && !updateOptions.allow) {
            setSearchParams({
                [generateSearchParam(basePath, 'mode')]: 'view'
            })
        }
    }, [mode])

    return (
        <div
            className={clsx(
                "w-full bg-white flex items-start justify-center shadow-xl"
            )}
        >
            <div
                className={clsx(
                    TABLE_SIZES[tableSize],
                    'w-full px-4 pb-4'
                )}
            >
                {renderContent()}
            </div>
        </div>
    );
};